import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  BatterySungageApplicationHandoffDto,
  CreateBatterySungageApplicationDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

const cacheHandoff = async (
  queryClient: ReturnType<typeof useQueryClient>,
  proposalId: string,
  handoff: BatterySungageApplicationHandoffDto,
): Promise<void> => {
  const applicationKey = queryKeys.batteryProposal.sungageApplication(
    proposalId,
    handoff.applicationId,
  )
  await queryClient.cancelQueries({ queryKey: applicationKey })
  queryClient.setQueryData(
    queryKeys.batteryProposal.financing(proposalId),
    handoff.financing,
  )
  queryClient.setQueryData(applicationKey, handoff.application)
}

/**
 * Reserve Now. Creates or recovers this Proposal's Sungage attempt and
 * returns the hosted borrower URL when the server has one.
 */
export default function useStartBatterySungageApplication(): UseMutationResult<
  BatterySungageApplicationHandoffDto,
  unknown,
  { proposalId: string; body: CreateBatterySungageApplicationDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      batteryProposalService.startSungageApplication(proposalId, body),
    onSuccess: (handoff, { proposalId }) =>
      cacheHandoff(queryClient, proposalId, handoff),
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.financing(proposalId),
      })
    },
  })
}

export { cacheHandoff as cacheSungageApplicationHandoff }
